import { type z } from 'zod';

import { makeListQuery } from '../../common/list-query/list-query';
import { ViolationBehaviorRuleSchema } from '../violation-behavior-rule/violation-behavior-rule';
import { ViolationSeveritySchema } from '../violation-severity/violation-severity';
import { ViolationStatusSchema } from '../violation-status/violation-status';
import { ViolationSubjectKindSchema } from '../violation-subject-kind/violation-subject-kind';
import { ViolationQueueReasonSchema } from '../violation/violation';
import { ViolationTypeKeySchema } from '../violation-type/violation-type';

/**
 * The whitelisted filters of the violations queue (N7).
 *
 * Six, and each earns its place by being how a reviewer actually batches work:
 * `status` separates what needs deciding from what is decided (the service
 * defaults to the open set, never this schema — the reasoning
 * `ReportsListQuerySchema` states); `severity` is what a shift starts with;
 * `typeKey` works a run of the same category together; `queueReason` is the
 * operational one — a queue filling with `ai_unavailable` is an outage, and
 * being unable to isolate it would hide a platform failure inside a work list.
 *
 * `subjectKind` and `behaviorRule` arrived with the behavioural detector (P27):
 * a reviewer triages counted patterns as a class, and one rule at a time. The
 * rule filter overlaps `typeKey` on purpose — a behavioural type's key IS its
 * rule — because `typeKey`'s picker reads the catalogue under
 * `violations.manage`, which the seeded `moderator` role does not hold, while a
 * closed enum of three rules is something every `violations.review` holder can
 * be offered without a lookup.
 *
 * A rule alone **implies** the behavioural kind (the row's CHECK already binds
 * the two). A rule *against* message subjects is a contradiction, and it is
 * refused with a 422 naming the field rather than answered with an empty page a
 * reviewer would read as "no cases" (the `VenueSlotsListQuerySchema` shape: the
 * rule lives in the contract, once).
 *
 * There is deliberately **no member filter**, for the reason the reports queue
 * has none: `offenderUserId` would turn a work list into a per-member dossier.
 * The number a reviewer needs about a member travels on the detail
 * (`ViolationDetail.standingViolations`, and the behavioural `subject.history`).
 */
export const ViolationsListQuerySchema = makeListQuery({
  status: ViolationStatusSchema.optional(),
  severity: ViolationSeveritySchema.optional(),
  typeKey: ViolationTypeKeySchema.optional(),
  queueReason: ViolationQueueReasonSchema.optional(),
  subjectKind: ViolationSubjectKindSchema.optional(),
  behaviorRule: ViolationBehaviorRuleSchema.optional(),
}).refine((query) => !(query.subjectKind === 'message' && query.behaviorRule !== undefined), {
  error: 'behaviorRule applies to behavioural subjects only',
  path: ['behaviorRule'],
});

export type ViolationsListQuery = z.infer<typeof ViolationsListQuerySchema>;
